/**
 * The Tasks screen's calendar state, in the URL.
 *
 *   /tasks?view=calendar&cal=week&date=2026-10-22&robot=NomNom&hours=full
 *   /tasks?view=calendar&cal=agenda&date=2026-10-30&days=5
 *
 * So a link to "NomNom's Thursday" can be pasted into a chat, the back
 * button undoes a page or a drill-down, and a reload keeps the place.
 * Every parameter is optional and a bad value reads as its default --
 * a hand-edited URL must never take the screen down.
 *
 * THE VIEW (2026-10-30). With no `cal=`, the calendar opens on the view
 * this browser last used (`calendar-prefs.ts`), Day on a first visit --
 * the caller passes that as `fallbackCal`. So that a link copied from
 * the calendar opens the same view for whoever receives it, the calendar
 * view always writes `cal=` explicitly, Day included.
 */
import { isDateKey, type DateKey } from './lane-time';

export type TasksView = 'table' | 'calendar';
export type CalendarViewKey = 'day' | 'week' | 'month' | 'agenda';

export function isCalendarViewKey(v: unknown): v is CalendarViewKey {
  return v === 'day' || v === 'week' || v === 'month' || v === 'agenda';
}

/** The Agenda's range: the chosen day and the next (2026-10-30), grown
 *  three days at a time by "Show next 3 days", never past a fortnight. */
export const AGENDA_DEFAULT_DAYS = 2;
export const AGENDA_STEP_DAYS = 3;
export const AGENDA_MAX_DAYS = 14;

export function clampAgendaDays(n: number): number {
  if (!Number.isFinite(n)) return AGENDA_DEFAULT_DAYS;
  return Math.max(AGENDA_DEFAULT_DAYS, Math.min(AGENDA_MAX_DAYS, Math.floor(n)));
}

export interface CalendarUrlState {
  view: TasksView;
  cal: CalendarViewKey;
  /** Null = today, on the site's calendar. */
  date: DateKey | null;
  robot: string;
  /** The whole day, rather than the working hours. */
  fullDay: boolean;
  /** How many days the Agenda shows from `date` (`days=`, Agenda only). */
  days: number;
}

export const DEFAULT_CAL_VIEW: CalendarViewKey = 'day';

export function parseCalendarUrl(
  search: string | URLSearchParams,
  fallbackCal: CalendarViewKey = DEFAULT_CAL_VIEW,
): CalendarUrlState {
  const p = typeof search === 'string' ? new URLSearchParams(search) : search;
  const cal = p.get('cal');
  const date = p.get('date');
  return {
    view: p.get('view') === 'calendar' ? 'calendar' : 'table',
    cal: isCalendarViewKey(cal) ? cal : fallbackCal,
    date: isDateKey(date) ? date : null,
    robot: (p.get('robot') ?? '').slice(0, 80),
    fullDay: p.get('hours') === 'full',
    days: p.has('days') ? clampAgendaDays(Number(p.get('days'))) : AGENDA_DEFAULT_DAYS,
  };
}

/**
 * `prev` with `patch` applied. Parameters this module does not own are
 * kept; its own are written only when they differ from the default, so
 * the plain Tasks URL stays `/tasks` -- except the calendar view, which
 * the calendar always names (see the module comment). `fallbackCal` is
 * the view `prev` shows when it names none.
 */
export function writeCalendarUrl(
  prev: URLSearchParams,
  patch: Partial<CalendarUrlState>,
  fallbackCal: CalendarViewKey = DEFAULT_CAL_VIEW,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  const state = { ...parseCalendarUrl(prev, fallbackCal), ...patch };
  const set = (k: string, v: string | null) => (v === null ? next.delete(k) : next.set(k, v));
  set('view', state.view === 'calendar' ? 'calendar' : null);
  set('cal', state.view === 'calendar' ? state.cal : null);
  set('date', state.view === 'calendar' && state.date ? state.date : null);
  set('robot', state.view === 'calendar' && state.robot.trim() ? state.robot.trim() : null);
  set('hours', state.view === 'calendar' && state.fullDay ? 'full' : null);
  const days = clampAgendaDays(state.days);
  set(
    'days',
    state.view === 'calendar' && state.cal === 'agenda' && days !== AGENDA_DEFAULT_DAYS
      ? String(days)
      : null,
  );
  return next;
}
